// src/admin/api/rolesApi.js
//
// Role catalog API for the admin pages. Same module shape as
// admin/api/permissionsApi.js, mirroring
// presentation/shared/access_provisioning/rbac_controller.py:
//
//   GET    /api/auth/roles       → {items, total, page, page_size, total_pages}
//   POST   /api/auth/roles       → {name, level, description, permission_names}
//   PUT    /api/auth/roles/{id}  → partial; permission_names REPLACES the set
//   DELETE /api/auth/roles/{id}  → soft-deactivate, returns the record
//
// api/authApi.js keeps its server-paginated listRoles(), which UsersPage's
// role picker still calls with a `level` filter. This module is the
// whole-list + writes surface the admin page needs; the two don't overlap.

import { api } from "../../api/client";
import { toAdminError } from "./adminApiError";
import { fetchAllPages } from "./fetchAllPages";

/**
 * The four levels a role can attach at, in widest-to-narrowest scope order —
 * domain/identity/value_objects/role_level.py's RoleLevel.
 *
 * Note CreateRoleRequest's `level` field description says "One of: platform,
 * org, project", omitting ACCOUNT. That docstring is stale: the enum, the
 * ORM binding tables, and the old form all carry four. Following the enum.
 */
export const ROLE_LEVELS = [
  { value: "platform", label: "Platform — every organization" },
  { value: "org", label: "Organization — everywhere in one org" },
  { value: "account", label: "Account — one customer account" },
  { value: "project", label: "Project — one engagement" },
];

// snake_case → camelCase here and nowhere else. `permissions` is already a
// flat list of permission NAMES on RoleResponse, not {id, name} objects.
function toRole(dto) {
  return {
    id: dto.id,
    name: dto.name,
    level: dto.level,
    description: dto.description || "",
    isActive: Boolean(dto.is_active),
    permissions: dto.permissions || [],
  };
}

/**
 * Every role, across all pages — the admin list sorts/searches/paginates it
 * client-side through DataTable. `level`, when given, narrows the catalog
 * server-side to that one RoleLevel.
 */
export async function listRoles({ level } = {}) {
  try {
    const params = level ? { level } : {};
    return (await fetchAllPages("/auth/roles", params)).map(toRole);
  } catch (err) {
    throw toAdminError(err, "Failed to load roles.");
  }
}

export async function createRole({ name, level, description, permissionNames }) {
  try {
    const res = await api.post("/auth/roles", {
      name: name.trim(),
      level,
      description: description?.trim() || "",
      permission_names: permissionNames,
    });
    return toRole(res.data);
  } catch (err) {
    throw toAdminError(err, "Could not create the role.", "name");
  }
}

/**
 * `level` is deliberately not sent: a role is strictly single-level and fixed
 * once created (see RoleLevel's docstring — the attachment table differs per
 * level, so changing it would orphan existing bindings). The endpoint would
 * accept it; the form doesn't offer it.
 *
 * `permission_names` REPLACES the role's whole set rather than adding to it,
 * which is why the picker always submits the full selection.
 */
export async function updateRole(id, { name, description, permissionNames }) {
  try {
    const res = await api.put(`/auth/roles/${id}`, {
      name: name.trim(),
      description: description?.trim() || "",
      permission_names: permissionNames,
    });
    return toRole(res.data);
  } catch (err) {
    throw toAdminError(err, "Could not update the role.", "name");
  }
}

/** Soft-deactivate (is_active=false) — the row stays and existing grants keep referencing it. */
export async function deactivateRole(id) {
  try {
    const res = await api.delete(`/auth/roles/${id}`);
    return toRole(res.data);
  } catch (err) {
    throw toAdminError(err, "Could not deactivate the role.");
  }
}
